/**
 * user-engagement.js — Kenaz GTM / GA4 event tracking
 *
 * Requires analytics.js to be loaded first (provides window.user_engagement, window.scroll_event).
 *
 * Tracks (user_engagement event):
 *  1. Notes section viewed       (.essence-notes-section)         — fires once
 *  3. Note card clicked          (.essence-note-item)
 *  4. Info box slider viewed     (.info-box-item)                 — re-fires on scroll
 *  4b. Info box slider clicked
 *  5. Video with text viewed     (.video-with-text-video)         — re-fires on scroll
 *  5b. Video mute / unmute       (.mute-button)
 *  7. Collapsible text viewed + clicked  (.product__accordion)
 *  8. Power meter section viewed + clicked (.power-meter-section)
 *  9. Discount section viewed + clicked (.product-discount-section)
 *
 * Tracks (scroll_event - separate event):
 *  6. Scroll depth milestones    (25 / 50 / 75 / 100 %)          — once per page load
 */

(function () {
  'use strict';

  function init() {
    // Guard: analytics utility must be ready
    if (typeof window.user_engagement !== 'function') {
      console.warn('[Kenaz] user-engagement.js loaded before analytics.js — events will not fire.');
      return;
    }

    var pageLoadTime = Date.now();

    /* ─── Helper ──────────────────────────────────────────────────── */
    function getScrollDepth() {
      var scrollable = document.body.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 100;
      return Math.round((window.scrollY / scrollable) * 100);
    }

    /* ─── 1. Notes Section Viewed — fires ONCE when section enters viewport ─── */
    var notesSection = document.querySelector('.essence-notes-section');

    if (notesSection) {
      var notesSectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          window.user_engagement({
            action: 'viewed',
            object: 'note_card',
            sub_params: {
              total_notes: document.querySelectorAll('.essence-note-item').length
            }
          });

          notesSectionObserver.unobserve(entry.target); // fire once only
        });
      }, { threshold: 0.5 });

      notesSectionObserver.observe(notesSection);
    }

    /* ─── 3. Note Card Clicked ────────────────────────────────────── */
    /* index+1 gives 1-based position of the card in the DOM          */
    document.querySelectorAll('.essence-note-item').forEach(function (card, index) {
      card.addEventListener('click', function () {
        window.user_engagement({
          action: 'clicked',
          object: 'note_card',
          sub_params: {
            note_name:     card.dataset.noteName || null,
            note_position: index + 1,
            click_type: 'tap'
          }
        });
      });
    });

    /* ─── 4. Info Box Slider Items Viewed (.info-box-item) ──────── */
    /* No Set guard — re-fires each time item re-enters viewport     */
    var infoItemObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        window.user_engagement({
          action: 'viewed',
          object: 'info_box_slider',
          sub_params: {
            banner_id:       entry.target.dataset.bannerId || null,
            banner_position: Number(entry.target.dataset.position) || null
          }
        });
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.info-box-item').forEach(function (item) {
      infoItemObserver.observe(item);
    });

    // 4b. Info Box Slider — Clicked
    document.querySelectorAll('.info-box-item').forEach(function (item, index) {
      item.addEventListener('click', function () {
        window.user_engagement({
          action: 'clicked',
          object: 'info_box_slider',
          sub_params: {
            banner_id:       item.dataset.bannerId || null,
            banner_position: index + 1
          }
        });
      });
    });

    /* ─── 5. Video with Text — Viewed (replaces "played") ─────────── */
    /* Autoplay video — track when it enters viewport, not play event */
    var videoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var vid = entry.target;
        window.user_engagement({
          action: 'viewed',
          object: 'video',
          sub_params: {
            video_id:    vid.dataset.videoId || null,
            video_title: vid.dataset.title   || null,
            section:     'video_with_text'
          }
        });
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.video-with-text-video').forEach(function (vid) {
      videoObserver.observe(vid);
    });

    /* ─── 5b. Video Mute / Unmute ──────────────────────────────────── */
    /* At click time vid.muted reflects state BEFORE toggle:
       muted=true  → user is unmuting → action: 'unmute'
       muted=false → user is muting   → action: 'mute'               */
    document.querySelectorAll('.mute-button').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var wrapper = btn.closest('.video-wrapper');
        var vid     = wrapper ? wrapper.querySelector('.video-with-text-video') : null;

        window.user_engagement({
          action: (vid && vid.muted) ? 'unmute' : 'mute',
          object: 'video',
          sub_params: {
            video_id:    vid ? (vid.dataset.videoId || null) : null,
            video_title: vid ? (vid.dataset.title   || null) : null,
            section:     'video_with_text'
          }
        });
      });
    });

    /* ─── 6. Scroll Depth Milestones ──────────────────────────────── */
    /* Fires only once per milestone per page load (Set guard kept)   */
    /* Uses separate scroll_event for better GA4 segmentation         */
    var milestones      = [25, 50, 75, 100];
    var firedMilestones = new Set();

    window.addEventListener('scroll', function () {
      var depth = getScrollDepth();

      milestones.forEach(function (m) {
        if (depth >= m && !firedMilestones.has(m)) {
          firedMilestones.add(m);

          window.scroll_event({
            depth_percentage:  m,
            time_to_scroll_ms: Date.now() - pageLoadTime
          });
        }
      });
    }, { passive: true });

    /* ─── 7. Collapsible Text — Viewed + Clicked ──────────────────── */
    /* Targets .product__accordion (accordion-tab) on PDP            */

    // 7a. Viewed — re-fires each time accordion re-enters viewport
    var collapsibleObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var heading     = entry.target.querySelector('.accordion__title');
        var headingText = heading ? heading.textContent.trim() : null;

        window.user_engagement({
          action: 'viewed',
          object: 'collapsible_text',
          sub_params: {
            heading: headingText
          }
        });
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('.product__accordion').forEach(function (tab) {
      collapsibleObserver.observe(tab);
    });

    // 7b. Clicked — open or close action, with heading text
    document.querySelectorAll('.product__accordion summary').forEach(function (summary) {
      summary.addEventListener('click', function () {
        var details     = summary.closest('details');
        var heading     = summary.querySelector('.accordion__title');
        var headingText = heading ? heading.textContent.trim() : null;
        // details.open is state BEFORE toggle: open=true means user is closing
        var action      = (details && details.open) ? 'close' : 'open';

        window.user_engagement({
          action: action,
          object: 'collapsible_text',
          sub_params: {
            heading: headingText
          }
        });
      });
    });

    // 7c. Accordion Content Viewed — fires when expanded content enters viewport
    var accordionContentObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var accordion   = entry.target.closest('.product__accordion');
        var heading     = accordion ? accordion.querySelector('.accordion__title') : null;
        var headingText = heading ? heading.textContent.trim() : null;

        window.user_engagement({
          action: 'viewed',
          object: 'collapsible_content',
          sub_params: {
            heading: headingText
          }
        });
      });
    }, { threshold: 0.3 });

    document.querySelectorAll('.product__accordion .accordion__content').forEach(function (content) {
      accordionContentObserver.observe(content);
    });

    // 7d. Accordion Content Clicked — captures text of clicked element within content
    document.querySelectorAll('.product__accordion .accordion__content').forEach(function (content) {
      content.addEventListener('click', function (e) {
        var accordion   = content.closest('.product__accordion');
        var heading     = accordion ? accordion.querySelector('.accordion__title') : null;
        var headingText = heading ? heading.textContent.trim() : null;
        var clickedText = e.target.textContent.trim().replace(/\s+/g, ' ') || null;

        window.user_engagement({
          action: 'clicked',
          object: 'collapsible_content',
          sub_params: {
            heading:      headingText,
            clicked_text: clickedText
          }
        });
      });
    });

    /* ─── 8. Power Meter Section — Viewed + Clicked ───────────────── */

    // 8a. Viewed — re-fires each time section re-enters viewport
    var powerMeterSection = document.querySelector('.power-meter-section');

    if (powerMeterSection) {
      var powerMeterObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          window.user_engagement({
            action: 'viewed',
            object: 'Concentration Banner',
            sub_params: {
              scroll_depth: getScrollDepth()
            }
          });
        });
      }, { threshold: 0.5 });

      powerMeterObserver.observe(powerMeterSection);

      // 8b. Clicked — any click within the power meter section
      powerMeterSection.addEventListener('click', function () {
        window.user_engagement({
          action: 'clicked',
          object: 'Concentration Banner',
          sub_params: {
            scroll_depth: getScrollDepth()
          }
        });
      });
    }

    /* ─── 9. Product Discount Section — Viewed + Clicked ─────────── */

    // 9a. Viewed — re-fires each time section re-enters viewport
    var discountSection = document.querySelector('.product-discount-section');

    if (discountSection) {
      var discountSectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          window.user_engagement({
            action: 'viewed',
            object: 'discount_section',
            sub_params: {
              scroll_depth: getScrollDepth()
            }
          });
        });
      }, { threshold: 0.5 });

      discountSectionObserver.observe(discountSection);

      // 9b. Clicked — captures text of the element the user clicked
      discountSection.addEventListener('click', function (e) {
        // Walk up from click target to find the nearest text-bearing container
        var textEl = e.target.closest('.prepaid-first-div, .extra-discount, .discount-description');
        var clickedText = null;

        if (textEl) {
          // Strip inner SVG text and collapse whitespace
          clickedText = textEl.innerText ? textEl.innerText.trim().replace(/\s+/g, ' ') : null;
        } else if (e.target.textContent) {
          clickedText = e.target.textContent.trim().replace(/\s+/g, ' ') || null;
        }

        window.user_engagement({
          action: 'clicked',
          object: 'discount_section',
          sub_params: {
            clicked_text: clickedText,
            scroll_depth: getScrollDepth()
          }
        });
      });
    }

  } // end init()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

